import { RoutePackageDTO } from 'api/model';
import React from 'react';
import styled from 'styled-components';
import {
  describeCandidateChoice,
  describeCandidateStatus,
  describeOtherStatus,
  describeSpareTime,
  PackageLabel,
} from './candidate-choice-helpers';

const Wrapper = styled.div`
  display: grid;
  gap: 4px;
  margin-top: 4px;
  font-size: 0.85em;
`;

const Muted = styled.span`
  color: rgb(120, 120, 120);
`;

const Details = styled.details`
  color: rgb(120, 120, 120);

  summary {
    cursor: pointer;
  }
`;

// width: 0 + min-width: 100 %: fyller kortet men bidrar inte med tabellens bredd till det, så
// att en bred tabell scrollar i sidled i stället för att trycka ut kortet.
const TableScroll = styled.div`
  width: 0;
  min-width: 100%;
  overflow-x: auto;
  margin-top: 4px;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  color: ${({ theme }) => theme.colors.foreground.primary};

  th,
  td {
    padding: 2px 8px 2px 0;
    text-align: left;
    white-space: nowrap;
    vertical-align: baseline;
  }

  th {
    font-weight: 600;
    color: rgb(120, 120, 120);
  }

  td.status {
    color: rgb(120, 120, 120);
  }
`;

const Row = styled.tr<{ chosen?: boolean; muted?: boolean }>`
  font-weight: ${({ chosen }) => (chosen ? 700 : 400)};
  opacity: ${({ muted }) => (muted ? 0.65 : 1)};
`;

const formatKm = (km: number | null | undefined) =>
  km == null ? '–' : `${Math.round(km)} km`;

type Props = {
  pkg: RoutePackageDTO;
  packageLabel: PackageLabel;
  /** Öppen från början — i ruttprovet är jämförelsen själva underlaget för betyget. */
  defaultOpen?: boolean;
  /** Bortfallet per orsak, under tabellen. */
  footer?: React.ReactNode;
};

/**
 * "Varför Natthaphong?" — det avgörande skälet till rank 1, och de andra som fanns att välja på
 * med avstånd, dag och tillgänglighet (ruttfeedback 2026-10-28, Feedbackknapp-1). Underlag för
 * trafikledarens bedömning; ändrar ingenting i förslaget.
 */
const CandidateChoicePanel: React.FC<Props> = ({
  pkg,
  packageLabel,
  defaultOpen = false,
  footer,
}) => {
  const top = pkg.candidates.find((c) => c.rank === 1);
  if (!top) return null;

  const sentence = describeCandidateChoice(pkg, packageLabel);
  const alternatives = pkg.candidates.filter((c) => c.userId !== top.userId);
  const others = pkg.otherFieldTesters ?? [];
  const compared = alternatives.length + others.length;

  return (
    <Wrapper>
      {sentence && (
        <div>
          <b>Varför {top.fullName}?</b> {sentence}
        </div>
      )}
      {(compared > 0 || footer) && (
        <Details open={defaultOpen}>
          <summary>
            {compared > 0
              ? `Jämför med ${compared} andra fälttestare`
              : 'Övriga fälttestare'}
          </summary>
          {compared > 0 && (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th>Fälttestare</th>
                    <th>Status</th>
                    <th title="Fågelväg från startpunkten till första upphämtningen, plus en eventuell betald resa till startpunkten — det fördelningen jämför">
                      Avstånd
                    </th>
                    <th>Dag</th>
                    <th>Tillgänglig</th>
                    <th>Efter dagen</th>
                  </tr>
                </thead>
                <tbody>
                  {[top, ...alternatives].map((c) => (
                    <Row chosen={c.userId === top.userId} key={`c-${c.userId}`}>
                      <td>
                        {c.fullName}
                        {c.isFullTime ? <Muted> · heltid</Muted> : null}
                      </td>
                      <td className="status">
                        {c.userId === top.userId
                          ? 'vald'
                          : (describeCandidateStatus(c, packageLabel) ??
                            `rank ${c.rank}`)}
                      </td>
                      <td>{formatKm(c.proximityKm)}</td>
                      <td>
                        {c.estimatedDayHours != null
                          ? `${c.estimatedDayHours.toFixed(1).replace('.', ',')} h`
                          : '–'}
                      </td>
                      <td>{c.availabilityWindow ?? '–'}</td>
                      <td>
                        {describeSpareTime(c.spareMinutesAfterDay) ?? '–'}
                      </td>
                    </Row>
                  ))}
                  {others.map((o) => (
                    <Row
                      key={`o-${o.userId}`}
                      muted={!o.fitsSchedule && o.rankOneInPackageIndex == null}
                    >
                      <td>
                        {o.fullName}
                        {o.isFullTime ? <Muted> · heltid</Muted> : null}
                      </td>
                      <td className="status">
                        {describeOtherStatus(o, packageLabel)}
                      </td>
                      <td>{formatKm(o.proximityKm)}</td>
                      <td>–</td>
                      <td>{o.availabilityWindow ?? '–'}</td>
                      <td>–</td>
                    </Row>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
          {footer}
        </Details>
      )}
    </Wrapper>
  );
};

export default CandidateChoicePanel;
